import PropTypes from 'prop-types';
import {
  forwardRef,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Empty } from 'antd';
import { get } from 'lodash-es';
import VirtualList from 'rc-virtual-list';
import { FIELD_ENUMS, isField } from '@icp/form-schema';
import { Loading } from '@icp/components';
import { shouldTranslateByDefault } from '@icp/settings';
import { setFieldInitialValue } from '@icp/form-renderer-core';
import RecursionRenderer from '../RecursionRenderer';
import { MultiDataSourceTypes, SingleDataSourceTypes } from '../propTypes';
import {
  useClassName,
  useDataSourceSupportLazy,
  useDisplayValue,
  useHttpBody,
  useVariablePattern,
} from '../hooks';
import { withFieldWrapper } from '../fieldWrapper';
import { useElementDecorator, useIsInDesign } from '../FormRenderCtx';
import { CurrentDataProvider } from '../currentDataCtx';
import { useDispatch, useSelector } from '../store';
import { ListFieldPathProvider, useListFieldPath } from '../listFieldPathCtx';

const EMPTY_DATA = [{}];
const EMPTY_FIELDS = [];
const EMPTY_PROPS = {};

// memo 跳过数据未变的 item 子树，防止在性能很差的浏览器上只是 show 一个 loading 都因为要重新渲染要卡很久。
/* eslint-disable react/prop-types */
const ListItem = memo(
  forwardRef(function ListItem(
    { itemValue, itemProps, itemStyle, itemChildren, fields, keyPath, itemField, gapStyle },
    ref,
  ) {
    return (
      <CurrentDataProvider value={itemValue}>
        <div
          ref={ref}
          {...itemProps}
          className={clsx('list-item', itemProps.className)}
          style={{ ...itemStyle, ...itemProps.style, ...gapStyle }}
        >
          {itemChildren || <RecursionRenderer fields={fields} keyPath={`${keyPath},fields`} />}
          {isValidElement(itemField)
            ? itemField
            : itemField &&
              isField(itemField) && (
                <RecursionRenderer
                  fields={[itemField]}
                  keyPath={`${keyPath},componentProps,itemField`}
                />
              )}
        </div>
      </CurrentDataProvider>
    );
  }),
);
/* eslint-enable react/prop-types */

function disableScroll(el) {
  const scrollbarWidth = el.offsetWidth - el.clientWidth;
  if (!scrollbarWidth) return;
  el.style.overflow = 'hidden';
  el.style.paddingRight = `${scrollbarWidth}px`;
}

function enableScroll(el) {
  el.style.overflow = null;
  el.style.paddingRight = null;
}

const ListElement = forwardRef(function ListElement(props, ref) {
  const {
    children,
    keyPath,
    id,
    valueField,
    notUseIdToResolveValue,
    className: classNameProp,
    style,
    fields = EMPTY_FIELDS,
    componentProps = EMPTY_PROPS,
  } = props;

  const {
    itemField, // deprecated
    direction = 'column', // deprecated
    wrap = false, // deprecated
    empty = false,
    gap, // deprecated
    style: wrapperStyle,
    itemStyle, // deprecated
    itemProps = EMPTY_PROPS,
    dataSource,
    dataUrl,
    dataUrlResolveMode,
    dataFilters,
    sortModel,
    dataResponseKeyPath = 'results',
    transformDataResponse,
    translateDataResponse = shouldTranslateByDefault(),
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody: httpBodyProp,
    selectColId,
    multiDataSource,
    multiDataSourceFlat = false,
    lazyLoading = false,
    cacheBlockSize = 30,
    searchText: searchTextProp,
    defaultValues, // deprecated
    LoadingProps,
    virtual = false,
    itemHeight: itemHeightProp,
    syncToFormData = false,
  } = componentProps;

  // 综合判断实际布局方向：style.flexDirection > componentProps.style.flexDirection > deprecated direction
  // 只有纵向布局（column）且不换行时才允许虚拟滚动
  const effectiveDirection = style?.flexDirection || wrapperStyle?.flexDirection || direction;
  const effectiveWrap = style?.flexWrap || wrapperStyle?.flexWrap || (wrap ? 'wrap' : 'nowrap');
  const isVertical = effectiveDirection === 'column';
  const isNoWrap = effectiveWrap === 'nowrap';
  const enableVirtual = virtual && isVertical && isNoWrap;

  const searchText = useVariablePattern(searchTextProp) || undefined;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);
  const httpBody = useHttpBody(httpBodyProp);

  const dispatch = useDispatch();
  const parentListFieldPath = useListFieldPath();

  const [refresh, setRefresh] = useState({});

  const nodeRef = useRef(null);
  const virtualListRef = useRef(null);

  const valueProp = useDisplayValue(id, valueField, notUseIdToResolveValue);
  const { loading, dataFetched, moreLoading, hasMore, loadMore } = useDataSourceSupportLazy({
    skip: isInDesign || valueProp,
    refresh,
    // lazyLoading 只支持但数据源
    lazy: lazyLoading && !multiDataSource,
    searchText,
    debounceLeading: !searchText,
    cacheBlockSize,
    defaultValues, // deprecated
    // single data source config
    dataSource,
    dataUrl,
    dataResponseKeyPath,
    dataFilters,
    sortModel,
    transformDataResponse,
    translateDataResponse,
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody,
    selectColId,
    // multi config
    multiDataSource,
    multiDataSourceFlat,
    dataUrlResolveMode,
  });

  const value = isInDesign ? EMPTY_DATA : valueProp || dataFetched;

  // 当 syncToFormData 开启后，优先使用 store 中的值（因为用户可能已经编辑过了）
  const storeValue = useSelector((state) => {
    if (!syncToFormData || !id || isInDesign) return undefined;
    const resolvedKeyPath = parentListFieldPath ? `${parentListFieldPath}.${id}` : id;
    return get(state.formData.values, resolvedKeyPath);
  });
  const effectiveValue = syncToFormData && storeValue ? storeValue : value;

  // 当 syncToFormData 开启且数据来自 dataSource 时，将数据同步到表单 store，
  // 使得 List 内部的可编辑子组件（Input、Select 等）可以通过路径拼接读写 store。
  const syncedRef = useRef(false);
  useEffect(() => {
    if (syncToFormData && id && dataFetched && !valueProp && !isInDesign) {
      const resolvedKeyPath = parentListFieldPath ? `${parentListFieldPath}.${id}` : id;
      dispatch(setFieldInitialValue({ keyPath: resolvedKeyPath, initialValue: dataFetched }));
      syncedRef.current = true;
    }
  }, [syncToFormData, id, dataFetched, valueProp, isInDesign, dispatch, parentListFieldPath]);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
      getData: () => effectiveValue,
      refresh: () => setRefresh({}),
    }),
    [effectiveValue],
  );

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (itemField) {
        console.warn(`'itemField' is deprecated, use 'fields' instead`);
      }
      if (itemStyle) {
        console.warn(`'itemStyle' is deprecated, use 'itemProps' instead`);
      }
    }
  }, [itemField, itemStyle]);

  useEffect(() => {
    if (loading) {
      disableScroll(nodeRef.current);
    } else {
      enableScroll(nodeRef.current);
      if (virtual) {
        virtualListRef.current?.scrollTo({ top: 0 });
      } else {
        nodeRef.current.scrollTop = 0;
      }
    }
  }, [loading, virtual]);

  const noChildren = !itemField && !children && !fields?.length;

  const handleScroll = useCallback(
    (event) => {
      if (!lazyLoading) return;
      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const isBottom = scrollHeight - scrollTop - clientHeight < 32 * 2;
      if (isBottom && hasMore && !moreLoading) {
        loadMore();
      }
    },
    [lazyLoading, hasMore, moreLoading, loadMore],
  );

  // 测量容器高度，用于传给 rc-virtual-list 的 height prop。
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (!enableVirtual || !nodeRef.current) return undefined;

    const measure = () => {
      const el = nodeRef.current;
      if (!el) return;
      const h = el.clientHeight;
      if (h > 0) {
        setContainerHeight((prev) => (prev !== h ? h : prev));
      }
    };

    measure();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(nodeRef.current);

    return () => {
      if (ro) ro.disconnect();
    };
  }, [enableVirtual]);

  // itemHeight 是 rc-virtual-list 对未渲染 item 的高度预估值，偏大不会跳动，偏小会跳动
  const itemHeight = itemHeightProp || 200;

  // 虚拟滚动下用 paddingBottom 模拟 gap
  const effectiveGap = gap || style?.gap || wrapperStyle?.gap;
  const virtualGapStyle = useMemo(
    () => (enableVirtual && effectiveGap ? { paddingBottom: effectiveGap } : undefined),
    [enableVirtual, effectiveGap],
  );

  const renderItem = (itemValue, itemIndex) => {
    // 计算当前 item 的 basePath，支持嵌套 List
    let itemBasePath = null;
    if (syncToFormData && id) {
      itemBasePath = parentListFieldPath
        ? `${parentListFieldPath}.${id}.${itemIndex}`
        : `${id}.${itemIndex}`;
    }

    const itemKey = itemValue.id || itemIndex;
    const gapStyle =
      itemIndex < (effectiveValue ? effectiveValue.length - 1 : 0) ? virtualGapStyle : undefined;

    const listItem = (
      <ListItem
        key={itemKey}
        itemValue={itemValue}
        itemProps={itemProps}
        itemStyle={itemStyle}
        itemChildren={children}
        fields={fields}
        keyPath={keyPath}
        itemField={itemField}
        gapStyle={gapStyle}
      />
    );

    if (itemBasePath) {
      return (
        <ListFieldPathProvider key={itemKey} basePath={itemBasePath}>
          {listItem}
        </ListFieldPathProvider>
      );
    }

    return listItem;
  };

  const renderList = () => {
    if (!Array.isArray(effectiveValue)) return null;

    // 虚拟滚动模式 — 只挂载可视区域内的 item
    if (enableVirtual && containerHeight > 0) {
      return (
        <VirtualList
          data={effectiveValue}
          height={containerHeight}
          itemHeight={itemHeight}
          itemKey={(item, index) => item.id || index}
          onScroll={handleScroll}
          ref={virtualListRef}
        >
          {(itemValue, itemIndex) => renderItem(itemValue, itemIndex)}
        </VirtualList>
      );
    }

    // 普通渲染模式 — 挂载所有 item
    return effectiveValue.map((itemValue, itemIndex) => renderItem(itemValue, itemIndex));
  };

  return (
    <ElementDecorator keyPath={keyPath}>
      <div
        className={clsx(
          'list-element form-element',
          `direction-${direction}`,
          {
            'no-children': noChildren,
            wrap,
          },
          className,
          classNameComp,
        )}
        style={{ gap, ...style, ...wrapperStyle }}
        onScroll={!enableVirtual && lazyLoading ? handleScroll : undefined}
        ref={nodeRef}
      >
        {loading ? (
          <Loading
            overlay={true}
            delayed={false}
            {...LoadingProps}
            style={{ top: nodeRef.current?.scrollTop }}
          />
        ) : null}
        {!loading && empty && effectiveValue?.length === 0 && <Empty {...empty} />}
        {renderList()}
        {lazyLoading && dataFetched && moreLoading ? <Loading size={16} delayed={false} /> : null}
      </div>
    </ElementDecorator>
  );
});

ListElement.propTypes = {
  children: PropTypes.node,
  keyPath: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueField: PropTypes.string,
  notUseIdToResolveValue: PropTypes.bool,
  className: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
    /**
     * @deprecated, 使用 fields 替代
     * List 里每个元素的 json schema，支持使用所有 form 的元素
     */
    itemField: PropTypes.shape({
      component: PropTypes.oneOf(FIELD_ENUMS),
      componentProps: PropTypes.shape({}),
      fields: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    /**
     * @deprecated，使用 style.flexDirection 替代
     */
    direction: PropTypes.oneOf(['row', 'column']),
    /**
     * @deprecated，使用 style.flexWrap 替代
     */
    wrap: PropTypes.bool,
    /**
     * @deprecated，使用 style.gap 替代
     */
    gap: PropTypes.number,
    /**
     * 当无数据的时候显示的占位符，antd 的 Empty 的属性
     * @default false
     */
    empty: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        // `Empty` props, see https://ant.design/components/empty-cn/#API
      }),
    ]),
    /**
     * @deprecated
     */
    itemStyle: PropTypes.shape({}),
    /**
     * 传给每个子元素顶层 div 的属性
     */
    itemProps: PropTypes.shape({}),
    /**
     * @deprecated
     */
    defaultValues: PropTypes.arrayOf(PropTypes.shape({})),
    LoadingProps: PropTypes.shape({}),
    ...SingleDataSourceTypes,
    /**
     * 当 httpMethod 为 post 时，请求的 body 内容
     */
    httpBody: PropTypes.string,
    ...MultiDataSourceTypes,
    /**
     * 是否开启懒加载
     */
    lazyLoading: PropTypes.bool,
    /**
     * 懒加载每次请求的数据量
     */
    cacheBlockSize: PropTypes.number,
    /**
     * 指定模糊搜索文本
     */
    searchText: PropTypes.string,
    /**
     * 是否开启虚拟滚动，开启后只渲染可视区域内的列表项。
     * 需要容器有明确的 CSS 高度（height 或 max-height）。
     * @default false
     */
    virtual: PropTypes.bool,
    /**
     * 虚拟滚动时每个列表项的预估高度（px），用于计算滚动区域。
     * 不需要精确，rc-virtual-list 会在渲染后自动修正。
     * 不传时默认 200px。
     */
    itemHeight: PropTypes.number,
    /**
     * 开启后，dataSource 拉取的数据会同步到表单 store 中（以 List 的 id 为 key），
     * List 内部的可编辑子组件（Input、Select 等）的值修改会写回 store，提交表单时会包含这些数据。
     * 使用此功能时 List 必须配置 id。
     * @default false
     */
    syncToFormData: PropTypes.bool,
  }),
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
ListElement.displayName = 'List';

export default withFieldWrapper(ListElement);
